import { observer } from 'mobx-react'
import React, { ReactElement } from 'react'
import { ToastContainer } from 'react-toastify'

import {
   defaultTheme as designSystemDefaultTheme,
   ThemeContextProvider as DesignSystemThemeProvider
} from '@flowwai-frontend/design-system'
import {
   LOGIN_ROUTE_PATH,
   removeSearchAndMonitoringConfig
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import {
   apolloClientConfig,
   region,
   subscriptionUrl,
   url
} from '@flowwai-frontend/bps-officer-web/graphql'
import { setIsSessionLogoutModalOpen } from '@flowwai-frontend/bps-officer-web/login'
import {
   APISauceCreateWithAuthProvider,
   ApolloClientWithAuthProvider,
   getAuthLink,
   InvalidAuthSessionError,
   useAuthSession
} from '@flowwai-frontend/shared/auth/common'

import {
   AppThemeProvider,
   defaultTheme
} from '@flowwai-frontend/bps-officer-web/style-guide'
import { Routes } from './Routes/Routes'
import StoresProvider from './StoresProvider'

const ProvidersAndRoutesWithAuth = (): ReactElement => {
   const {
      getAccessToken: getAccessTokenFn,
      clearAccessTokenCookie
   } = useAuthSession()

   const getAccessToken = async (): Promise<string | void> => {
      try {
         const accessToken = await getAccessTokenFn()

         return accessToken
      } catch (error) {
         if (error instanceof InvalidAuthSessionError) {
            window.location.href = LOGIN_ROUTE_PATH

            removeSearchAndMonitoringConfig()
            setIsSessionLogoutModalOpen(true)
         }
      }
   }
   const link = getAuthLink({
      url,
      subscriptionUrl,
      mutationsWithSubscriptions: [
         'sendQueryStreamedDataInThread',
         'sendQueryResponseInThread',
         'applicationUpdatedDummyMutation',
         'feeUpdatedDummyMutation'
      ],
      region,
      getAccessToken,
      clearAccessTokenCookie
   })

   return (
      <ApolloClientWithAuthProvider config={{ ...apolloClientConfig, link }}>
         <APISauceCreateWithAuthProvider
            getAccessToken={getAccessToken}
            clearAccessTokenCookie={clearAccessTokenCookie}
         >
            <StoresProvider>
               <AppThemeProvider theme={defaultTheme} colorScheme='light'>
                  <DesignSystemThemeProvider theme={designSystemDefaultTheme}>
                     <>
                        <div className='relative overflow-hidden h-full'>
                           <Routes />
                        </div>
                        <ToastContainer
                           position='bottom-center'
                           autoClose={1000}
                           hideProgressBar={true}
                           newestOnTop={true}
                           closeOnClick
                           rtl={false}
                           draggable
                           pauseOnHover
                        />
                     </>
                  </DesignSystemThemeProvider>
               </AppThemeProvider>
            </StoresProvider>
         </APISauceCreateWithAuthProvider>
      </ApolloClientWithAuthProvider>
   )
}

export default observer(ProvidersAndRoutesWithAuth)
